import Link from "next/link";
import { ReactNode, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { DropEntry } from "../dropTypes";
import { ImageSlideOverlay } from "../layouts/landing/ImageSlideOverlay";
import { IMAGE_SIZES } from "../lib/constants";
import {
  mediumScreenAndBelow,
  smallScreen,
  xSmallScreen,
} from "../lib/mediaQuery";
import { ArweaveImage } from "./ArweaveImage";
import { Flex } from "./Grid";

interface Props {
  drops?: DropEntry[];
  durationSecs?: number;
  progressBars?: boolean;
}

export const ImageCarousel = ({
  drops,
  durationSecs = 10,
  progressBars = true,
}: Props) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [pauseTimer, setPauseTimer] = useState<boolean>(false);
  const [resetTimer, setResetTimer] = useState<boolean>(false);

  useEffect(() => {
    let timer;

    if (!pauseTimer) {
      timer = setInterval(() => {
        setCurrentSlideIndex((prevIndex) => {
          return (prevIndex + 1) % drops?.length;
        });
      }, durationSecs * 1000);
    }

    if (resetTimer) {
      clearInterval(timer);
      timer = setInterval(() => {
        setCurrentSlideIndex((prevIndex) => {
          return (prevIndex + 1) % drops?.length;
        });
      }, durationSecs * 1000);
      setResetTimer(false);
    }
    return () => {
      clearInterval(timer);
    };
  }, [drops?.length, pauseTimer, resetTimer]);

  return (
    <>
      <SpotlightContainer>
        {drops?.map((drop, index) => (
          <Link href={`/drop/${drop?.id}`}>
            <Slide
              key={index}
              imageUrl={drop?.data?.image}
              isActive={currentSlideIndex === index}
            >
              <ImageSlideOverlay
                drop={drop}
                index={index}
                setPauseTimer={setPauseTimer}
              />
            </Slide>
          </Link>
        ))}
      </SpotlightContainer>
      {progressBars && drops?.length > 1 && (
        <AllProgressBarsContainer>
          {drops?.map((_, index) => (
            <ProgressBarContainer
              flex={1}
              data-is-active={currentSlideIndex === index}
              onClick={() => {
                setCurrentSlideIndex(index);
                setResetTimer(true);
              }}
            >
              <ProgressBar key={index}>
                <ProgressOverlay duration={durationSecs} />
              </ProgressBar>
            </ProgressBarContainer>
          ))}
        </AllProgressBarsContainer>
      )}
    </>
  );
};

interface SlideProps {
  imageUrl?: string;
  isActive?: boolean;
  children?: ReactNode;
}

export const Slide = ({ imageUrl, isActive, children }: SlideProps) => {
  return (
    <SlideImageContainer data-is-active={isActive}>
      <ArweaveImage
        fill
        sizes={IMAGE_SIZES}
        alt="Spotlight image"
        src={imageUrl}
        css={`
          object-fit: cover;
          filter: blur(10px);
          ${mediumScreenAndBelow(css`
            filter: none;
          `)}
        `}
      />
      <RadialGradientOverlay />
      {children}
    </SlideImageContainer>
  );
};

const SpotlightContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const RadialGradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(0, 0, 0, 0.01) 0%,
    rgba(0, 0, 0, 0.45) 100%
  );
`;

const SlideImageContainer = styled.div`
  justify-content: center;
  position: relative;
  overflow: hidden;
  outline: 0px solid transparent;
  outline-offset: 4px;
  border-radius: 24px;
  ${xSmallScreen(
    css`
      border-radius: 16px;
    `
  )}
  width: 100%;
  opacity: 0.5;
  transition: opacity 0.5s ease-in-out;
  &[data-is-active="true"] {
    opacity: 1;
    height: 520px;
    ${smallScreen(
      css`
        height: 500px;
      `
    )}
    ${xSmallScreen(
      css`
        height: 300px;
      `
    )}
  }
`;

const AllProgressBarsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-md);
  width: 100%;
  gap: var(--spacing-xs);
`;

const ProgressBarContainer = styled(Flex)`
  cursor: pointer;
  padding: var(--spacing-sm) 0;
  &[data-is-active="true"] {
    cursor: default;
  }
  ${xSmallScreen(css`
    padding: var(--spacing-xs) 0;
  `)}
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 5px;
  background-color: var(--progress-bar-bg);
  border-radius: 10px;
`;

const ProgressOverlay = styled.div<{ duration: number }>`
  width: 0;
  height: inherit;
  border-radius: 10px;
  [data-is-active="true"] & {
    animation: ${(p) =>
      p.duration
        ? `fillAnimation ${p.duration}s linear`
        : "fillAnimation 10s linear"};
    animation-delay: 0;
    animation-fill-mode: forwards;
    background-color: var(--progress-bar);
    width: 100%;
  }
  @keyframes fillAnimation {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
`;
